import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUser,
  FaBriefcase,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaComments,
  FaExclamationCircle,
  FaCheckCircle,
} from "react-icons/fa";

const BRAND = {
  yellow: "#fff000",
  black: "#000000",
  white: "#ffffff",
};

const norm = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[_\s-]/g, "");

const getJobId = (job) =>
  job?.id ||
  job?.job_id ||
  job?.jobId ||
  null;

const getJobTitle = (job) =>
  job?.service ||
  job?.service_name ||
  job?.serviceName ||
  job?.title ||
  job?.job_title ||
  "Scheduled Job";

const getClientName = (job) =>
  job?.client?.full_name ||
  job?.client?.name ||
  job?.client?.fullName ||
  job?.client_name ||
  job?.clientName ||
  job?.customer?.full_name ||
  job?.customer?.name ||
  "Client";

const getLocation = (job) =>
  job?.location ||
  job?.address ||
  job?.job_location ||
  job?.job_address ||
  job?.location_name ||
  "Location not specified";

const getDescription = (job) =>
  job?.description ||
  job?.job_description ||
  "";

const getStatus = (job) =>
  job?.status ||
  job?.job_status ||
  "Scheduled";

const getScheduledDateTime = (job) => {
  const directFields = [
    job?.scheduled_at,
    job?.scheduledAt,
    job?.scheduled_datetime,
    job?.scheduledDateTime,
    job?.appointment_at,
    job?.appointmentAt,
    job?.appointment_datetime,
    job?.appointmentDateTime,
    job?.start_at,
    job?.startAt,
  ];

  for (const value of directFields) {
    if (value) {
      const date = new Date(value);

      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
  }

  const dateValue =
    job?.scheduled_date ||
    job?.scheduledDate ||
    job?.appointment_date ||
    job?.appointmentDate ||
    job?.date;

  const timeValue =
    job?.scheduled_time ||
    job?.scheduledTime ||
    job?.appointment_time ||
    job?.appointmentTime ||
    job?.time;

  if (dateValue) {
    const combined = timeValue
      ? `${dateValue}T${timeValue}`
      : dateValue;

    const date = new Date(combined);

    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
};

const isScheduledJob = (job) => {
  const scheduled = getScheduledDateTime(job);

  if (!scheduled) return false;

  const status = norm(getStatus(job));

  const excludedStatuses = [
    "cancelled",
    "canceled",
    "completed",
    "done",
    "paid",
    "rejected",
  ];

  return !excludedStatuses.includes(status);
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const formatTime = (date) => {
  if (!date) return "Time not specified";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatFullDate = (date) => {
  if (!date) return "Date not specified";

  return date.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatShortDate = (date) => {
  if (!date) return "—";

  return date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getRelativeDayLabel = (date) => {
  const today = new Date();
  const tomorrow = new Date();

  tomorrow.setDate(today.getDate() + 1);

  if (isSameDay(date, today)) {
    return "Today";
  }

  if (isSameDay(date, tomorrow)) {
    return "Tomorrow";
  }

  return formatFullDate(date);
};

const getScheduleStatus = (date) => {
  const now = new Date();

  if (date < now) {
    return {
      label: "Started",
      background: "#f5f5f5",
      color: "#555555",
      icon: <FaClock size={10} />,
    };
  }

  const diff = date.getTime() - now.getTime();
  const hours = diff / (1000 * 60 * 60);

  if (hours <= 2) {
    return {
      label: "Starting soon",
      background: BRAND.yellow,
      color: BRAND.black,
      icon: <FaExclamationCircle size={10} />,
    };
  }

  return {
    label: "Upcoming",
    background: BRAND.black,
    color: BRAND.yellow,
    icon: <FaCheckCircle size={10} />,
  };
};

const addDays = (date, amount) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

function ScheduleTab({
  jobs = [],
  onViewJob,
  onMessageClient,
  addToast,
}) {
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, 60);

    return () => clearTimeout(timer);
  }, []);

  const scheduledJobs = useMemo(() => {
    return jobs
      .map((job) => ({
        ...job,
        _scheduledDate: getScheduledDateTime(job),
      }))
      .filter(
        (job) =>
          job._scheduledDate &&
          isScheduledJob(job)
      )
      .sort(
        (a, b) =>
          a._scheduledDate.getTime() -
          b._scheduledDate.getTime()
      );
  }, [jobs]);

  const selectedDayJobs = useMemo(() => {
    return scheduledJobs.filter((job) =>
      isSameDay(
        job._scheduledDate,
        selectedDate
      )
    );
  }, [scheduledJobs, selectedDate]);

  const upcomingJobs = useMemo(() => {
    const now = new Date();

    return scheduledJobs.filter(
      (job) => job._scheduledDate >= now
    );
  }, [scheduledJobs]);

  const todayJobs = useMemo(() => {
    const today = new Date();

    return scheduledJobs.filter((job) =>
      isSameDay(
        job._scheduledDate,
        today
      )
    );
  }, [scheduledJobs]);

  const tomorrowJobs = useMemo(() => {
    const tomorrow = addDays(new Date(), 1);

    return scheduledJobs.filter((job) =>
      isSameDay(
        job._scheduledDate,
        tomorrow
      )
    );
  }, [scheduledJobs]);

  const goPreviousDay = () => {
    setSelectedDate((current) =>
      addDays(current, -1)
    );
  };

  const goNextDay = () => {
    setSelectedDate((current) =>
      addDays(current, 1)
    );
  };

  const goToday = () => {
    setSelectedDate(new Date());
  };

  const handleViewJob = (job) => {
    if (typeof onViewJob === "function") {
      onViewJob(job);
      return;
    }

    addToast?.(
      `Opening Job #${getJobId(job) || ""}`,
      "info",
      "Job"
    );
  };

  const handleMessageClient = (job) => {
    if (typeof onMessageClient === "function") {
      onMessageClient(job);
      return;
    }

    addToast?.(
      "Messaging will open from the Messages section.",
      "info",
      "Messages"
    );
  };

  const summaryCards = [
    {
      label: "Today",
      value: todayJobs.length,
      icon: <FaCalendarAlt />,
      background: BRAND.yellow,
      color: BRAND.black,
    },
    {
      label: "Tomorrow",
      value: tomorrowJobs.length,
      icon: <FaClock />,
      background: BRAND.black,
      color: BRAND.yellow,
    },
    {
      label: "Upcoming",
      value: upcomingJobs.length,
      icon: <FaBriefcase />,
      background: BRAND.yellow,
      color: BRAND.black,
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap');

        .schedule-wrap {
          font-family: 'Raleway', sans-serif;
          color: ${BRAND.black};
          width: 100%;
        }

        .schedule-wrap * {
          box-sizing: border-box;
        }

        .schedule-enter {
          opacity: 0;
          transform: translateY(18px);
          transition:
            opacity .5s ease,
            transform .5s cubic-bezier(.34,1.3,.64,1);
        }

        .schedule-enter.show {
          opacity: 1;
          transform: translateY(0);
        }

        .schedule-header {
          background: ${BRAND.black};
          border-radius: 20px;
          padding: 2rem 2.25rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          position: relative;
          overflow: hidden;
        }

        .schedule-header::before {
          content: '';
          position: absolute;
          width: 220px;
          height: 220px;
          border-radius: 50%;
          right: -80px;
          top: -100px;
          background: rgba(255,240,0,.14);
        }

        .schedule-header::after {
          content: '';
          position: absolute;
          width: 160px;
          height: 160px;
          border-radius: 50%;
          left: -100px;
          bottom: -100px;
          background: rgba(255,240,0,.08);
        }

        .schedule-header-content {
          position: relative;
          z-index: 1;
        }

        .schedule-header-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: ${BRAND.yellow};
          font-size: .78rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .7px;
          margin-bottom: 7px;
        }

        .schedule-header-title {
          color: ${BRAND.white};
          font-size: 1.75rem;
          font-weight: 800;
          margin: 0;
          letter-spacing: -.5px;
        }

        .schedule-header-subtitle {
          color: #d1d1d1;
          font-size: .84rem;
          margin: 7px 0 0;
          max-width: 600px;
        }

        .schedule-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .schedule-summary-card {
          background: ${BRAND.white};
          border: 1px solid #e6e6e6;
          border-radius: 16px;
          padding: 1.15rem;
          box-shadow: 0 2px 10px rgba(0,0,0,.04);
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .schedule-summary-icon {
          width: 42px;
          height: 42px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .schedule-summary-label {
          color: #777777;
          font-size: .72rem;
          font-weight: 700;
        }

        .schedule-summary-value {
          color: ${BRAND.black};
          font-size: 1.35rem;
          font-weight: 800;
          margin-top: 1px;
        }

        .schedule-panel {
          background: ${BRAND.white};
          border: 1px solid #e6e6e6;
          border-radius: 18px;
          box-shadow: 0 2px 10px rgba(0,0,0,.04);
          margin-bottom: 1.5rem;
          overflow: hidden;
        }

        .schedule-panel-header {
          padding: 1.15rem 1.4rem;
          border-bottom: 1px solid #eeeeee;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .schedule-date-controls {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .schedule-nav-btn {
          width: 34px;
          height: 34px;
          border: 1px solid #dddddd;
          background: ${BRAND.white};
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: ${BRAND.black};
          transition: all .15s;
        }

        .schedule-nav-btn:hover {
          border-color: ${BRAND.yellow};
          color: ${BRAND.black};
          background: ${BRAND.yellow};
        }

        .schedule-today-btn {
          border: 1px solid #dddddd;
          background: ${BRAND.white};
          color: ${BRAND.black};
          padding: 7px 12px;
          border-radius: 9px;
          font-family: 'Raleway', sans-serif;
          font-size: .75rem;
          font-weight: 800;
          cursor: pointer;
          transition: all .15s;
        }

        .schedule-today-btn:hover {
          border-color: ${BRAND.yellow};
          background: ${BRAND.yellow};
        }

        .schedule-date-title {
          font-size: .92rem;
          font-weight: 800;
          color: ${BRAND.black};
        }

        .schedule-list {
          padding: 1rem;
        }

        .schedule-job {
          display: grid;
          grid-template-columns: 82px minmax(0, 1fr) auto;
          gap: 1rem;
          padding: 1.1rem;
          border: 1px solid #eeeeee;
          border-radius: 14px;
          margin-bottom: .75rem;
          transition:
            transform .15s,
            box-shadow .15s,
            border-color .15s;
          background: ${BRAND.white};
        }

        .schedule-job:last-child {
          margin-bottom: 0;
        }

        .schedule-job:hover {
          transform: translateY(-1px);
          border-color: ${BRAND.yellow};
          box-shadow: 0 5px 18px rgba(0,0,0,.06);
        }

        .schedule-time {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: 3px;
        }

        .schedule-time-value {
          color: ${BRAND.black};
          font-size: .9rem;
          font-weight: 800;
          white-space: nowrap;
        }

        .schedule-time-line {
          width: 1px;
          height: 30px;
          background: ${BRAND.yellow};
          margin-top: 7px;
        }

        .schedule-job-main {
          min-width: 0;
        }

        .schedule-job-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 5px;
        }

        .schedule-job-title {
          font-size: .95rem;
          font-weight: 800;
          color: ${BRAND.black};
          margin: 0;
        }

        .schedule-job-number {
          color: #888888;
          font-size: .7rem;
          font-weight: 700;
        }

        .schedule-status {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 9px;
          border-radius: 50px;
          font-size: .67rem;
          font-weight: 800;
        }

        .schedule-job-details {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 8px;
        }

        .schedule-job-detail {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #666666;
          font-size: .73rem;
          font-weight: 600;
        }

        .schedule-job-detail svg {
          color: ${BRAND.black};
          flex-shrink: 0;
        }

        .schedule-job-description {
          color: #777777;
          font-size: .72rem;
          line-height: 1.5;
          margin: 9px 0 0;
          max-width: 600px;
        }

        .schedule-job-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 7px;
          flex-wrap: wrap;
        }

        .schedule-action-btn {
          border: 1px solid #dddddd;
          background: ${BRAND.white};
          color: ${BRAND.black};
          padding: 7px 10px;
          border-radius: 8px;
          font-family: 'Raleway', sans-serif;
          font-size: .7rem;
          font-weight: 800;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all .15s;
        }

        .schedule-action-btn:hover {
          border-color: ${BRAND.yellow};
          color: ${BRAND.black};
          background: ${BRAND.yellow};
        }

        .schedule-action-btn.primary {
          background: ${BRAND.black};
          color: ${BRAND.white};
          border-color: ${BRAND.black};
        }

        .schedule-action-btn.primary:hover {
          background: ${BRAND.yellow};
          border-color: ${BRAND.yellow};
          color: ${BRAND.black};
        }

        .schedule-empty {
          text-align: center;
          padding: 3.5rem 1.5rem;
        }

        .schedule-empty-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 1rem;
          border-radius: 18px;
          background: ${BRAND.yellow};
          color: ${BRAND.black};
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .schedule-empty-title {
          font-size: .95rem;
          font-weight: 800;
          color: ${BRAND.black};
          margin: 0;
        }

        .schedule-empty-text {
          font-size: .78rem;
          color: #777777;
          margin: .4rem auto 0;
          max-width: 450px;
          line-height: 1.5;
        }

        .schedule-upcoming-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: .9rem 1.4rem;
          border-bottom: 1px solid #eeeeee;
        }

        .schedule-upcoming-row:last-child {
          border-bottom: none;
        }

        .schedule-upcoming-date {
          width: 54px;
          height: 54px;
          border-radius: 12px;
          background: ${BRAND.yellow};
          color: ${BRAND.black};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .schedule-upcoming-day {
          font-size: 1.1rem;
          font-weight: 800;
          line-height: 1;
        }

        .schedule-upcoming-month {
          font-size: .58rem;
          font-weight: 800;
          text-transform: uppercase;
          margin-top: 3px;
        }

        .schedule-upcoming-info {
          min-width: 0;
          flex: 1;
        }

        .schedule-upcoming-title {
          font-size: .82rem;
          font-weight: 800;
          color: ${BRAND.black};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;
        }

        .schedule-upcoming-meta {
          color: #777777;
          font-size: .68rem;
          margin-top: 3px;
        }

        @media (max-width: 768px) {
          .schedule-header {
            padding: 1.5rem;
          }

          .schedule-header-title {
            font-size: 1.4rem;
          }

          .schedule-job {
            grid-template-columns: 1fr;
            gap: .7rem;
          }

          .schedule-time {
            align-items: flex-start;
            flex-direction: row;
            gap: 8px;
          }

          .schedule-time-line {
            display: none;
          }

          .schedule-job-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 480px) {
          .schedule-summary {
            grid-template-columns: 1fr;
          }

          .schedule-panel-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .schedule-date-controls {
            width: 100%;
            justify-content: space-between;
          }

          .schedule-date-title {
            font-size: .82rem;
          }
        }
      `}</style>

      <div className="schedule-wrap">
        <div
          className={`schedule-header schedule-enter ${
            visible ? "show" : ""
          }`}
        >
          <div className="schedule-header-content">
            <div className="schedule-header-label">
              <FaCalendarAlt size={12} />
              Craftsman Schedule
            </div>

            <h1 className="schedule-header-title">
              Your Work Schedule
            </h1>

            <p className="schedule-header-subtitle">
              Keep track of your upcoming jobs, scheduled times,
              clients and work locations.
            </p>
          </div>
        </div>

        <div className="schedule-summary">
          {summaryCards.map((card, index) => (
            <div
              key={card.label}
              className={`schedule-summary-card schedule-enter ${
                visible ? "show" : ""
              }`}
              style={{
                transitionDelay: `${index * 70}ms`,
              }}
            >
              <div
                className="schedule-summary-icon"
                style={{
                  background: card.background,
                  color: card.color,
                }}
              >
                {card.icon}
              </div>

              <div>
                <div className="schedule-summary-label">
                  {card.label}
                </div>

                <div className="schedule-summary-value">
                  {card.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`schedule-panel schedule-enter ${
            visible ? "show" : ""
          }`}
          style={{
            transitionDelay: "140ms",
          }}
        >
          <div className="schedule-panel-header">
            <div className="schedule-date-title">
              {getRelativeDayLabel(selectedDate)}
            </div>

            <div className="schedule-date-controls">
              <button
                type="button"
                className="schedule-today-btn"
                onClick={goToday}
              >
                Today
              </button>

              <button
                type="button"
                className="schedule-nav-btn"
                onClick={goPreviousDay}
                aria-label="Previous day"
              >
                <FaChevronLeft size={11} />
              </button>

              <button
                type="button"
                className="schedule-nav-btn"
                onClick={goNextDay}
                aria-label="Next day"
              >
                <FaChevronRight size={11} />
              </button>
            </div>
          </div>

          <div className="schedule-list">
            {selectedDayJobs.length === 0 ? (
              <div className="schedule-empty">
                <div className="schedule-empty-icon">
                  <FaCalendarAlt />
                </div>

                <p className="schedule-empty-title">
                  No jobs scheduled for this day
                </p>

                <p className="schedule-empty-text">
                  When a job is assigned to you and given a
                  scheduled date and time, it will automatically
                  appear here.
                </p>
              </div>
            ) : (
              selectedDayJobs.map((job) => {
                const scheduledDate = job._scheduledDate;
                const scheduleStatus =
                  getScheduleStatus(scheduledDate);

                const jobId = getJobId(job);
                const jobTitle = getJobTitle(job);
                const clientName = getClientName(job);
                const location = getLocation(job);
                const description = getDescription(job);

                return (
                  <div
                    key={
                      jobId ||
                      `${jobTitle}-${scheduledDate.getTime()}`
                    }
                    className="schedule-job"
                  >
                    <div className="schedule-time">
                      <div className="schedule-time-value">
                        {formatTime(scheduledDate)}
                      </div>

                      <div className="schedule-time-line" />
                    </div>

                    <div className="schedule-job-main">
                      <div className="schedule-job-title-row">
                        <h3 className="schedule-job-title">
                          {jobTitle}
                        </h3>

                        {jobId && (
                          <span className="schedule-job-number">
                            Job #{String(jobId).padStart(4, "0")}
                          </span>
                        )}

                        <span
                          className="schedule-status"
                          style={{
                            background:
                              scheduleStatus.background,
                            color:
                              scheduleStatus.color,
                          }}
                        >
                          {scheduleStatus.icon}
                          {scheduleStatus.label}
                        </span>
                      </div>

                      <div className="schedule-job-details">
                        <span className="schedule-job-detail">
                          <FaUser size={10} />
                          {clientName}
                        </span>

                        <span className="schedule-job-detail">
                          <FaMapMarkerAlt size={10} />
                          {location}
                        </span>

                        <span className="schedule-job-detail">
                          <FaClock size={10} />
                          {formatShortDate(scheduledDate)}
                          {" · "}
                          {formatTime(scheduledDate)}
                        </span>
                      </div>

                      {description && (
                        <p className="schedule-job-description">
                          {description}
                        </p>
                      )}
                    </div>

                    <div className="schedule-job-actions">
                      <button
                        type="button"
                        className="schedule-action-btn"
                        onClick={() =>
                          handleMessageClient(job)
                        }
                      >
                        <FaComments size={10} />
                        Message
                      </button>

                      <button
                        type="button"
                        className="schedule-action-btn primary"
                        onClick={() =>
                          handleViewJob(job)
                        }
                      >
                        View Job
                        <FaArrowRight size={9} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div
          className={`schedule-panel schedule-enter ${
            visible ? "show" : ""
          }`}
          style={{
            transitionDelay: "220ms",
          }}
        >
          <div className="schedule-panel-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FaBriefcase
                size={12}
                style={{
                  color: BRAND.black,
                }}
              />

              <span
                style={{
                  fontWeight: 800,
                  fontSize: ".9rem",
                }}
              >
                Upcoming Work
              </span>
            </div>

            <span
              style={{
                color: "#777777",
                fontSize: ".72rem",
                fontWeight: 700,
              }}
            >
              {upcomingJobs.length} scheduled job
              {upcomingJobs.length !== 1 ? "s" : ""}
            </span>
          </div>

          {upcomingJobs.length === 0 ? (
            <div className="schedule-empty">
              <div className="schedule-empty-icon">
                <FaCalendarAlt />
              </div>

              <p className="schedule-empty-title">
                Your schedule is clear
              </p>

              <p className="schedule-empty-text">
                Upcoming jobs will appear here once clients
                schedule work with you.
              </p>
            </div>
          ) : (
            upcomingJobs
              .slice(0, 10)
              .map((job) => {
                const date = job._scheduledDate;
                const jobId = getJobId(job);

                return (
                  <div
                    key={
                      jobId ||
                      `${getJobTitle(job)}-${date.getTime()}`
                    }
                    className="schedule-upcoming-row"
                  >
                    <div className="schedule-upcoming-date">
                      <span className="schedule-upcoming-day">
                        {date.getDate()}
                      </span>

                      <span className="schedule-upcoming-month">
                        {date.toLocaleDateString([], {
                          month: "short",
                        })}
                      </span>
                    </div>

                    <div className="schedule-upcoming-info">
                      <p className="schedule-upcoming-title">
                        {getJobTitle(job)}
                      </p>

                      <p className="schedule-upcoming-meta">
                        {formatTime(date)}
                        {" · "}
                        {getClientName(job)}
                        {" · "}
                        {getLocation(job)}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="schedule-action-btn"
                      onClick={() =>
                        handleViewJob(job)
                      }
                    >
                      View
                      <FaArrowRight size={9} />
                    </button>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </>
  );
}

ScheduleTab.propTypes = {
  jobs: PropTypes.array,
  onViewJob: PropTypes.func,
  onMessageClient: PropTypes.func,
  addToast: PropTypes.func,
};

export default ScheduleTab;